import { CustomerGroupBody } from "../lib/models/CustomerGroup";
import { CustomerGroupListItem } from "../lib/models/CustomerGroupListItem";
import { DatatableBody } from "../lib/models/Datatable";
import { AgeGroupType } from "./AgeGroupType";
import { AreaType } from "./AreaType";
import { ComplaintType } from "./ComplaintType";
import { CustomerType } from "./CustomerType";
import { ElectricityFilter } from "./ElectricityFilter";
import { GasFilter } from "./GasFilter";
import { GlassFiberFilter } from "./GlassFiberFilter";
import { HeatingFilter } from "./HeatingFilter";
import { Score } from "./Score";
import { SupplyType } from "./SupplyType";
import { TernaryFilterOption } from "./TernaryFilterState";

export interface AuthState {
  readonly error?: string;
  readonly password: string;
  readonly token: string | null;
  readonly username: string;
  readonly role: string | null;
}

export interface EffectsColumnsDataState {
  effectsColumnsData: Array<{ columnName: string; columns: string[] }>;
}

export interface SalutationsState {
  salutationsData: Array<{ salutation: string }>;
}

export type Tariff = {
  tariffType: string;
  productName: string | null;
  supplyType: SupplyType | null;
  productFamily: string;
};

export type TariffTypesPayload = Array<
  Omit<Tariff, "supplyType"> & {
    supplyType: "Grundversorgung" | "Sondervertrag" | null;
  }
>;

export interface TariffTypesState {
  data: Tariff[];
}

export interface CustomerAddressPostalCodesState {
  data: string[];
  loading: boolean;
}

export interface MultiRequestState {
  pending: string | null;
  loading: boolean;
}
export interface DataState {
  readonly data?: any;
  readonly meta?: any;
  readonly loading: boolean;
}

export type ClusterState = {
  readonly data: Array<{ date: string; min: number; mid: number; max: number }>;
} & MultiRequestState;

export type LineChartsData = {
  timestampData: Array<{ date: string; avg: number; sum: number }>;
} & MultiRequestState;

export type EffectsDataState = {
  effectsData: Array<{
    column_name: string;
    effect: string;
    affected_rows: number;
    column_value: string;
    target_name: string;
    customer_type: string;
  }>;
} & MultiRequestState;

export interface DashboardFilterState {
  customerType: CustomerType[];
  dateRange: 6 | 12 | 24;
  electricityFilter: ElectricityFilter;
  gasFilter: GasFilter;
  heatingFilter: HeatingFilter;
  glassFiberFilter: GlassFiberFilter;
  score: Score;
  filterGroupsEnabled: {
    electricity: boolean;
    gas: boolean;
    heating: boolean;
    glassFiber: boolean;
  };
}

export type UserState = Array<{
  id: number;
  username: string;
  name: string;
  role: string;
}>;

export type PostalcodesState = {
  features: any[];

  type: "FeatureCollection";
} & MultiRequestState;

export interface EffectFilterState {
  columnName?: string;
  customerType: CustomerType;
}

export type CountState = {
  churnCount: number;
  churnCountBeginTimestamp: Date | null;
  churnCountEndTimestamp: Date | null;
  contractElectricityCount: number;
  contractGasCount: number;
  contractHeatingCount: number;
  contractGlassFiberCount: number;
  customerCount: number;
} & MultiRequestState;

export interface CustomerGroupFilterState {
  activeChurnedType: boolean;
  ageGroupType: AgeGroupType;
  complaintType: ComplaintType;
  customerAddressPostalCodes: Array<{ label: string; value: string }>;
  customerDuration: [number, number];
  customerType: CustomerType[];
  electricityFilter: ElectricityFilter;
  gasFilter: GasFilter;
  heatingFilter: HeatingFilter;
  glassFiberFilter: GlassFiberFilter;
  polygonMapSelection: Array<{ id: number; polygon: string }>;
  maxChurn: number;
  maxChurnDelta: number;
  maxClv: number;
  minChurn: number;
  minChurnDelta: number;
  minClv: number;
  nextBestActivityFilter: string[];
  salutation: string;
  optIn: OptInFilter;
  phone: TernaryFilterOption;
  email: TernaryFilterOption;
  housingSituationsFilter: {
    buildingTypes: number[];
    buildingAges: number[];
  };
  filterGroupsEnabled: {
    electricity: boolean;
    gas: boolean;
    heating: boolean;
    glassFiber: boolean;
    nextBestActivity: boolean;
    customerValueAnalysis: boolean;
    customerInformation: boolean;
    customerContactChannels: boolean;
    housingSituations: boolean;
  };
}

export interface OptInFilter {
  phone: TernaryFilterOption;
  mail: TernaryFilterOption;
  email: TernaryFilterOption;
}

export interface CustomerGroupFilterStateForQuery {
  active_churned_type: boolean;
  age_group_type: AgeGroupType;
  complaint_type: ComplaintType;
  customer_address_postal_codes: string[];
  customer_duration: [number, number];
  customer_type: CustomerType[];
  electricity_filter: ElectricityFilter;
  gas_filter: GasFilter;
  heating_filter: HeatingFilter;
  glass_fiber_filter: GlassFiberFilter;
  max_churn: number;
  max_churn_delta: number;
  max_clv: number;
  min_churn: number;
  min_churn_delta: number;
  min_clv: number;
  next_best_activity_filter: string[];
  polygon_map_selection: string;
  opt_in: OptInFilter;
  phone: TernaryFilterOption;
  email: TernaryFilterOption;
  housingSituationsFilter: {
    buildingTypes: number[];
    buildingAges: number[];
  };
  salutation: string;
  filterGroupsEnabled: {
    electricity: boolean;
    gas: boolean;
    heating: boolean;
    glassFiber: boolean;
    nextBestActivity: boolean;
    customerValueAnalysis: boolean;
    customerInformation: boolean;
    customerContactChannels: boolean;
    housingSituations: boolean;
  };
}

export interface ICustomerGroupExport {
  uuid: string;
  groupUuid: string;
  numResults: number;
  createdAt: Date;
  lastUpdateAt?: Date;
  comment?: string;
}

export interface MarketShareFilterState {
  areaType: AreaType[];
  selectedPostalCodes: Array<{ label: string; value: string }>;
}

export type MarketShareState = {
  postcodes: {
    features: any[];
    type: "FeatureCollection";
  };
  postcodesIA: string[];
} & MultiRequestState;

export type NbaDistributionState = {
  nbaTopTen: Array<{
    label: string;
    value: string;
  }>;
} & MultiRequestState;

export interface CustomerDensityState {
  features: any[];
  loading: boolean;
  type: "FeatureCollection";
}

export type PolygonDensityState = {
  polygons: Array<{
    id: number;
    count: number;
    countElectricity: number;
    countGas: number;
    countHeating: number;
    countGlassFiber: number;
  }>;
} & MultiRequestState;

export type CustomerGroupResultState = CustomerGroupBody & MultiRequestState;

export type LatLongState = {
  data: Array<{
    latitude: number;
    longitude: number;
    customerAddrStreet: string;
    customerAddrHouseNumber: string;
    countContractsGas: string;
    countContractsElectricity: string;
    countContractsDistrictHeating: string;
    countContractsGlassFiber: string;
  }>;
} & MultiRequestState;

export type CustomerGroups = { [uuid: string]: CustomerGroupListItem };

export type CustomerGroupListState = {
  payload: CustomerGroups;
  meta: { pending: boolean };
  error: boolean;
};

export type DatatableState = DatatableBody;

export enum Order {
  asc = "asc",
  desc = "desc",
}

export interface HousingSituationsState {
  buildingTypes: Array<{ id: number; name: string }>;
  buildingAges: Array<{ id: number; name: string }>;
}

export interface SupplyTypesMappedToProductFamiliesState {
  electricity: {
    [SupplyType.GV]: string[];
    [SupplyType.SV]: string[];
  };
  gas: {
    [SupplyType.GV]: string[];
    [SupplyType.SV]: string[];
  };
}

export interface ErrorPayload {
  path: string;
  message: string;
  stack: string;
  origin: string;
}

export interface ErrorState {
  errors: (ErrorPayload & { uuid: string })[];
}

export enum FeatureState {
  EXCLUDE = "EXCLUDE",
  INCLUDE = "INCLUDE",
}

export * from "./AgeGroupType";
export * from "./AreaType";
export * from "./ComplaintType";
export * from "./CustomerContacts";
export * from "./CustomerStatistics";
export * from "./CustomerType";
export * from "./ElectricityFilter";
export * from "./GasFilter";
export * from "./GlassFiberFilter";
export * from "./HeatingFilter";
export * from "./ProductType";
export * from "./RoleType";
export * from "./Score";
export * from "./SupplyType";
export * from "./TernaryFilterState";



// WEBPACK FOOTER //
// ./src/types/index.tsx